import { parseNumber } from './_data';
import type { CsvRowWithDataset, Dataset, DatasetPoint } from './_types';

const CONCAVE_MAX_EDGE_KM = 0.35;
const BUFFER_METERS = 8;

const cross = (
  o: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number }
): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

/** Build a convex hull from 2D points. */
const convexHull = (points: { x: number; y: number }[]) => {
  const sorted = [...points].sort((p1, p2) => {
    if (p1.x === p2.x) return p1.y - p2.y;
    return p1.x - p2.x;
  });

  if (sorted.length <= 2) return sorted;

  const lower: { x: number; y: number }[] = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper: { x: number; y: number }[] = [];
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const p = sorted[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }

  upper.pop();
  lower.pop();
  return lower.concat(upper);
};

/** Keep only the outer polygon and normalize geometry shape. */
const sanitizePolygonGeometry = (geometry: any, turf: any) => {
  if (!geometry) return geometry;

  if (geometry.type === 'Polygon') {
    if (geometry.coordinates?.length > 1) {
      return { type: 'Polygon', coordinates: [geometry.coordinates[0]] };
    }

    return geometry;
  }

  if (geometry.type === 'MultiPolygon') {
    let best = null;
    let bestArea = -1;

    for (const coords of geometry.coordinates || []) {
      const poly = { type: 'Polygon', coordinates: [coords[0]] };
      const area = turf?.area ? turf.area(poly) : 0;

      if (area > bestArea) {
        bestArea = area;
        best = poly;
      }
    }

    return best || geometry;
  }

  return geometry;
};

/** Build a polygon from grouped points (concave -> convex fallback). */
const buildPolygonForGroup = (
  points: DatasetPoint[],
  proj4: any,
  turf: any
): { geometry: any; hullType: string } | null => {
  if (!points.length) return null;

  const pointsLngLat = points.map((p) =>
    proj4('EPSG:25832', 'WGS84', [p.x, p.y])
  );
  const fc = turf.featureCollection(
    pointsLngLat.map((c: number[]) => turf.point(c))
  );

  const concave = turf.concave(fc, {
    maxEdge: CONCAVE_MAX_EDGE_KM,
    units: 'kilometers'
  });

  if (concave?.geometry) {
    const expanded = turf.buffer(concave, BUFFER_METERS, { units: 'meters' });
    if (expanded?.geometry) {
      return {
        geometry: sanitizePolygonGeometry(expanded.geometry, turf),
        hullType: 'concave+buffer'
      };
    }

    return {
      geometry: sanitizePolygonGeometry(concave.geometry, turf),
      hullType: 'concave'
    };
  }

  const convex = turf.convex(fc);
  if (convex?.geometry) {
    const expanded = turf.buffer(convex, BUFFER_METERS, { units: 'meters' });
    if (expanded?.geometry) {
      return {
        geometry: sanitizePolygonGeometry(expanded.geometry, turf),
        hullType: 'convex+buffer'
      };
    }

    return {
      geometry: sanitizePolygonGeometry(convex.geometry, turf),
      hullType: 'convex'
    };
  }

  const hull = convexHull(points);
  if (hull.length < 3) return null;

  const ring = hull.map((p) => {
    const [lng, lat] = proj4('EPSG:25832', 'WGS84', [p.x, p.y]);
    return [lng, lat];
  });

  ring.push(ring[0]);

  return {
    geometry: sanitizePolygonGeometry(
      { type: 'Polygon', coordinates: [ring] },
      turf
    ),
    hullType: 'convex'
  };
};

const buildFeatureFromGeometry = (
  dataset: Dataset,
  geometry: any,
  count: number,
  hullType: string
) => ({
  type: 'Feature',
  geometry,
  properties: {
    datasetId: dataset?.id || '',
    name: dataset?.name || 'Gebiet',
    color: dataset?.color || '#00693c',
    status: dataset?.status || 'Verteilnetz gebaut',
    count: count ?? 0,
    hullType: hullType || 'polygon'
  }
});

/** Convert CSV rows into polygons, grouped by dataset id. */
export const csvRowsToGeoJson = (
  rows: CsvRowWithDataset[],
  datasets: Dataset[],
  proj4: any,
  turf: any
) => {
  const grouped = new Map<string, DatasetPoint[]>();

  for (const row of rows) {
    const dataset = row._dataset || ({} as Dataset);
    const group = dataset.id || row['gebietsname'] || 'Unbekannt';
    const x = parseNumber(row['koordinate x']);
    const y = parseNumber(row['koordinate y']);

    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group).push({
      x,
      y,
      raw: row,
      dataset
    });
  }

  const features: any[] = [];

  for (const dataset of datasets) {
    const points = grouped.get(dataset.id) || [];
    if (points.length < 3) continue;

    const polygon = buildPolygonForGroup(points, proj4, turf);
    if (!polygon?.geometry) continue;

    features.push(
      buildFeatureFromGeometry(
        dataset,
        polygon.geometry,
        points.length,
        polygon.hullType
      )
    );
  }

  return {
    type: 'FeatureCollection',
    features
  };
};

/** Remove overlaps by clipping each feature against the accumulated union. */
export const applyNoOverlap = (features: any[], turf: any): any[] => {
  const callDifference = (a: any, b: any) => {
    // Turf v7 expects a FeatureCollection; older versions accepted (a, b).
    try {
      return turf.difference(turf.featureCollection([a, b]));
    } catch {
      return turf.difference(a, b);
    }
  };

  const callUnion = (a: any, b: any) => {
    // Turf v7 expects a FeatureCollection; older versions accepted (a, b).
    try {
      return turf.union(turf.featureCollection([a, b]));
    } catch {
      return turf.union(a, b);
    }
  };

  const result = [];
  let combined = null;

  for (const feature of features) {
    let current = feature;

    if (combined) {
      try {
        const diff = callDifference(current, combined);
        if (!diff || !diff.geometry) {
          continue;
        }

        current = {
          type: 'Feature',
          geometry: diff.geometry,
          properties: { ...feature.properties }
        };
      } catch {
        // If clipping fails, keep the original feature.
      }
    }

    result.push(current);

    try {
      combined = combined ? callUnion(combined, current) : current;
    } catch {
      // If union fails, continue without updating combined geometry.
    }
  }

  return result;
};

/** Draw a geojson feature collection on the map and fit bounds. */
export const addGeoJsonToMap = (
  map: any,
  L: any,
  geoJsonData: any,
  logDebug: (value: unknown) => void
) => {
  if (!geoJsonData || geoJsonData.type !== 'FeatureCollection') {
    logDebug({ error: 'Invalid GeoJSON', geoJsonData });
    return null;
  }

  const layer = L.geoJSON(geoJsonData, {
    style: (feature: any) => ({
      color: feature?.properties?.color || '#00693c',
      weight: 2,
      opacity: 1,
      fillColor: feature?.properties?.color || '#00693c',
      fillOpacity: 0.5
    }),
    onEachFeature: (feature: any, leafletLayer: any) => {
      const title = feature?.properties?.name || 'Gebiet';
      const status = feature?.properties?.status || 'Verteilnetz gebaut';
      const content = `<strong>${title}</strong><br>Gebietsstatus: ${status}`;

      leafletLayer.bindTooltip(content, { direction: 'top', sticky: true });
      leafletLayer.on('mouseover', () => leafletLayer.openTooltip());
      leafletLayer.on('mouseout', () => leafletLayer.closeTooltip());
    }
  }).addTo(map);

  const bounds = layer.getBounds();
  if (bounds && bounds.isValid()) {
    map.fitBounds(bounds, { padding: [20, 20] });
  }

  return layer;
};

/** Check whether a point is inside any polygon feature. */
export const pointInsidePolygons = (
  lng: number,
  lat: number,
  polygonsGeoJson: any,
  turf: any
) => {
  if (!polygonsGeoJson) return null;

  const point = turf.point([lng, lat]);

  for (const feature of polygonsGeoJson.features || []) {
    if (turf.booleanPointInPolygon(point, feature)) {
      return feature;
    }
  }

  return null;
};
